import { Component, inject, Input, ViewEncapsulation } from '@angular/core';
import {
  GlobalButtonDesign,
  GLOBAL_BUTTON_DESIGN_DEFAULT,
  TimerStatePreview,
  UpsellItemsSettings,
} from '../widget-preview.types';
import { CheckoutUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { SmartCartPreviewStateService } from '../../smart-cart-preview-state.service';
import {
  getActiveTimerPreviewState,
  getSampleTimerCountdown,
  getTimerPreviewStyle,
} from '../../../components/timer-config/timer.model';

const SAMPLE_VENDOR = 'Armani';
const SAMPLE_PRODUCT_TYPE = 'Flower';

const VARIANT_SELECTOR_ALWAYS_SHOW = 'inside';

// Design-box neutrals + base metrics — mirror the storefront runtime (upsellStyles.ts) so the preview
// matches. The admin has the token system, so neutrals resolve through --outline-default etc.
const BOX_OUTLINE = 'var(--outline-default)';
const CARD_SURFACE = 'var(--surface-container-lowest)';
const CARD_RADIUS_BASE = 8; // RADIUS_M
const IMAGE_RADIUS_BASE = 4; // RADIUS_S
const CARD_PADDING_CAROUSEL = 8;
const CARD_PADDING_LIST_COMPACT = 12;
const CAROUSEL_PRODUCTS_PER_VIEW = 2; // matches the runtime CAROUSEL_PRODUCTS_PER_VIEW (arrows window)

interface PreviewProduct {
  name: string;
  price: string;
  compareAt?: string;
  hasVariant?: boolean;
}

@Component({
  selector: 'upsell-items-preview',
  templateUrl: './upsell-items-preview.component.html',
  styleUrls: ['./upsell-items-preview.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class UpsellItemsPreviewComponent {
  @Input() settings!: UpsellItemsSettings;
  @Input() globalButtonDesign: GlobalButtonDesign = GLOBAL_BUTTON_DESIGN_DEFAULT;

  private readonly previewState = inject(SmartCartPreviewStateService);

  readonly mockProducts: PreviewProduct[] = [
    { name: 'BioGlow Vitamin C Face Cream', price: '13', compareAt: '18', hasVariant: true },
    { name: 'BioGlow Vitamin C Serum', price: '32' },
    { name: 'Oreange Delight Tanning Drops', price: '65', compareAt: '80' },
  ];

  readonly sampleVariantLabel = 'Color: Eggplant';

  get showVariantInline(): boolean {
    return (
      (this.settings?.productSelection?.settings?.variantSelector ?? VARIANT_SELECTOR_ALWAYS_SHOW) ===
      VARIANT_SELECTOR_ALWAYS_SHOW
    );
  }

  get displayType(): string {
    return this.settings?.productSelection?.style?.displayType ?? CheckoutUpsellExtension.DisplayType.CARDS;
  }

  get isCarousel(): boolean {
    return this.displayType === CheckoutUpsellExtension.DisplayType.CARDS;
  }

  get isCompact(): boolean {
    return this.displayType === CheckoutUpsellExtension.DisplayType.COMPACT;
  }

  get isList(): boolean {
    return this.displayType === CheckoutUpsellExtension.DisplayType.ROW;
  }

  get showCompareAt(): boolean {
    return this.settings?.productSelection?.settings?.showCompareAtPrice ?? true;
  }

  // List shows the merchant's row count (numberOfRowsInRowDisplayType, default 1); arrows page the rest.
  get listRows(): number {
    return Number(this.settings?.productSelection?.style?.numberOfRowsInRowDisplayType) || 1;
  }

  // Mirrors the runtime windowing: List renders `listRows` rows; Carousel with arrows shows a fixed
  // window of CAROUSEL_PRODUCTS_PER_VIEW (so the editor matches the storefront's default 2-up view);
  // Carousel-scroll shows the full set (scrollable). Compact renders a single card separately.
  get visibleProducts(): PreviewProduct[] {
    if (this.isList) return this.mockProducts.slice(0, this.listRows);
    if (this.isCarousel && !this.isCarouselScroll) return this.mockProducts.slice(0, CAROUSEL_PRODUCTS_PER_VIEW);
    return this.mockProducts;
  }

  get firstProduct(): PreviewProduct {
    return this.mockProducts[0];
  }

  get carouselNavStyle(): string {
    return (
      this.settings?.productSelection?.style?.carouselNavigationStyle ??
      CheckoutUpsellExtension.CarouselNavigationStyle.ARROWS
    );
  }

  get isCarouselScroll(): boolean {
    return this.isCarousel && this.carouselNavStyle === CheckoutUpsellExtension.CarouselNavigationStyle.SCROLL;
  }

  get showNav(): boolean {
    // Carousel-arrows, List, and Compact paginate via arrows; arrows show only when more than one
    // product doesn't fit in the current view.
    return (
      (this.isCarousel && !this.isCarouselScroll) ||
      (this.isList && this.mockProducts.length > this.listRows) ||
      (this.isCompact && this.mockProducts.length > 1)
    );
  }

  private get timer() {
    return this.settings?.timer;
  }

  get timerEnabled(): boolean {
    return !!this.timer?.enabled;
  }

  get placement(): string {
    return this.timer?.placement ?? 'inline';
  }

  get showInlineBar(): boolean {
    return this.timerEnabled && this.placement === 'inline';
  }

  get showBadge(): boolean {
    return this.timerEnabled && this.placement === 'badge';
  }

  private get isInTitle(): boolean {
    return this.timerEnabled && this.placement === 'in_title';
  }

  private get rawSectionTitle(): string {
    return this.settings?.productSelection?.settings?.sectionTitle ?? '<p>You may also like</p>';
  }

  // Empty keeps the title inheriting, exactly as the storefront does.
  get sectionTitleColor(): string {
    return this.settings?.productSelection?.settings?.sectionTitleColor ?? '';
  }

  get sectionTitleHtml(): string {
    return this.isInTitle ? this.rawSectionTitle.replace(/\{countdown\}/g, this.sampleCountdown) : this.rawSectionTitle;
  }

  get showAppendedCountdown(): boolean {
    return this.isInTitle && !this.rawSectionTitle.includes('{countdown}');
  }

  get timerMessageHtml(): string {
    return (this.activeTimerState?.message ?? '').replace(/\{countdown\}/g, this.sampleCountdown);
  }

  get timerIconUrl(): string {
    const icon = this.activeTimerState?.iconUrl;
    return typeof icon === 'string' ? icon : '';
  }

  get timerStyle(): Record<string, string> {
    return getTimerPreviewStyle(this.activeTimerState);
  }

  get countdown(): string {
    return this.sampleCountdown;
  }

  get addButtonLabel(): string {
    return this.settings?.productSelection?.settings?.textAddToOrder || 'Add to order';
  }

  get displayTexts(): string[] {
    const texts = this.settings?.productSelection?.settings?.additionalDisplayTexts ?? [];
    return texts
      .filter((text) => text.display)
      .map((text) => text.text.replace(/\{product-type\}/g, SAMPLE_PRODUCT_TYPE).replace(/\{vendor\}/g, SAMPLE_VENDOR));
  }

  private get buttonColors(): { bg: string; color: string; radius: number } {
    const s = this.settings?.productSelection?.settings;
    return {
      bg: s?.buttonBackgroundColor ?? this.globalButtonDesign.backgroundColor,
      color: s?.buttonTextColor ?? this.globalButtonDesign.textColor,
      radius: s?.buttonCornerRadius ?? this.globalButtonDesign.cornerRadius,
    };
  }

  get buttonStyle(): Record<string, string> {
    const { bg, color, radius } = this.buttonColors;
    return { 'background-color': bg, color, 'border-radius': `${radius}px` };
  }

  get navStyle(): Record<string, string> {
    const { bg, color } = this.buttonColors;
    return { 'background-color': bg, color };
  }

  get variantStyle(): Record<string, string> {
    return { 'border-color': this.buttonColors.bg };
  }

  private get style() {
    return this.settings?.productSelection?.style;
  }

  // Main Background Design box → the section wrapper. Mirrors runtime sectionStyle.
  get mainBackgroundStyle(): Record<string, string> {
    const bg = this.style?.mainBackground;
    const style: Record<string, string> = {
      'box-sizing': 'border-box',
      // Matches the runtime section: clip to the border-radius so a scrolling carousel can't drop the
      // rounded bottom corners.
      overflow: 'hidden',
      'background-color': bg?.color || 'transparent',
      'border-radius': `${bg?.cornerRadius ?? 0}px`,
      // null/undefined (the widget default) → the design base; an explicit 0 → 0 (no padding).
      padding: bg?.innerPadding != null ? `${bg.innerPadding}px` : '12px 20px',
    };
    if (bg?.borderWidth) style['border'] = `${bg.borderWidth}px solid ${BOX_OUTLINE}`;
    return style;
  }

  // Product Item Design box → each product card. Mirrors runtime productStyle common + padding base.
  // null (widget default) → base; an explicit 0 is honored (no border / square corners / no padding).
  get productItemStyle(): Record<string, string> {
    const box = this.style?.productItem;
    const basePadding = this.isCarousel ? CARD_PADDING_CAROUSEL : CARD_PADDING_LIST_COMPACT;
    const borderW = box?.borderWidth ?? 1; // null → 1px default; explicit 0 → no border
    const style: Record<string, string> = {
      'box-sizing': 'border-box',
      'background-color': box?.color || CARD_SURFACE,
      'border-radius': `${box?.cornerRadius ?? CARD_RADIUS_BASE}px`,
      padding: `${box?.innerPadding ?? basePadding}px`,
      // Always set border explicitly: an unset value (borderW = 1) draws the default, but an explicit 0
      // must emit `none` to override the `.upsell-preview__product` SCSS default border (else 0 leaks a
      // 1px border, diverging from the runtime which has no such default).
      border: borderW > 0 ? `${borderW}px solid ${BOX_OUTLINE}` : 'none',
    };
    return style;
  }

  // Image Design box → the product image box. Mirrors runtime imageWrapStyle (border/radius/padding + fit).
  get imageStyle(): Record<string, string> {
    const box = this.style?.image;
    const style: Record<string, string> = {
      'box-sizing': 'border-box',
      'border-radius': `${box?.cornerRadius ?? IMAGE_RADIUS_BASE}px`,
      // Reflect the image-fit setting: 'square' shows a 1:1 thumbnail; 'original' hints at a natural
      // (portrait) ratio. The preview has no real image, so 'original' uses a representative ratio.
      // Applies in Carousel (auto-height); List/Compact use fixed dimensions so the ratio is a no-op.
      'aspect-ratio': box?.fit === 'original' ? '3 / 4' : '1',
    };
    if (box?.borderWidth) style['border'] = `${box.borderWidth}px solid ${BOX_OUTLINE}`;
    if (box?.innerPadding) style['padding'] = `${box.innerPadding}px`;
    return style;
  }

  private get activeTimerState(): TimerStatePreview | undefined {
    return getActiveTimerPreviewState(this.timer, this.previewState.upsellPreviewTimerState());
  }

  private get sampleCountdown(): string {
    return getSampleTimerCountdown(this.previewState.upsellPreviewTimerState(), this.timer?.format);
  }
}
